import api from '../utils/api';

const BASE = '/api/scenarios';

/**
 * Get every scenario in the database (admin only).
 * @route GET /api/scenarios/all
 * @returns {Promise<Object[]>}
 */
export const getAllScenarios = () =>
    api.get(`${BASE}/all`).then((r) => r.data);

/**
 * Get the authenticated user's scenarios.
 * @route GET /api/scenarios/my
 * @returns {Promise<Object[]>}
 */
export const getMyScenarios = () =>
    api.get(`${BASE}/my`).then((r) => r.data);

/**
 * Get the authenticated user's published scenarios only.
 * @route GET /api/scenarios/my?published=true
 * @returns {Promise<Object[]>}
 */
export const getMyPublishedScenarios = () =>
    api.get(`${BASE}/my`, { params: { published: true } }).then((r) => r.data);

/**
 * Get all scenarios created by a specific user.
 * @route GET /api/scenarios/user/:userId
 * @param {string} userId
 * @returns {Promise<Object[]>} Empty array if the user has no scenarios
 */
export const getScenariosByUser = (userId) =>
    api.get(`${BASE}/user/${userId}`).then((r) => r.data);

/**
 * Get scenarios a user is a collaborator on (not creator of).
 * @route GET /api/scenarios/get-collaborative-scenarios/:userId
 * @param {string} userId
 * @returns {Promise<{ response: Object[] }>}
 */
export const getCollaborativeScenarios = (userId) =>
    api.get(`${BASE}/get-collaborative-scenarios/${userId}`, { withCredentials: true }).then((r) => r.data);

/**
 * Create a new scenario.
 * @route POST /api/scenarios
 * @param {string} title
 * @returns {Promise<Object>} The newly created scenario
 */
export const createScenario = (title) =>
    api.post(`${BASE}/`, { title }).then((r) => r.data);

/**
 * Delete a scenario and all associated data.
 * @route DELETE /api/scenarios/:scenarioId
 * @param {string} scenarioId
 * @returns {Promise<string>} Success message
 */
export const deleteScenario = (scenarioId) =>
    api.delete(`${BASE}/${scenarioId}`, { withCredentials: true }).then((r) => r.data);

/**
 * Check the password on a password-protected scenario.
 *
 * The comparison happens on the server against a bcrypt hash — the scenario GET
 * no longer returns the password at all, only a `hasPassword` flag.
 * @route POST /api/scenarios/:scenarioId/verify-password
 * @param {string} scenarioId
 * @param {string} password
 * @returns {Promise<{ valid: boolean }>}
 */
export const verifyScenarioPassword = (scenarioId, password) =>
    api.post(`${BASE}/${scenarioId}/verify-password`, { password }).then((r) => r.data);

/**
 * Full deep copy of a scenario including all content.
 * @route POST /api/scenarios/:scenarioId/copy
 * @param {string} scenarioId
 * @returns {Promise<Object>} The newly created scenario copy
 */
export const copyScenario = (scenarioId) =>
    api.post(`${BASE}/${scenarioId}/copy`, {}, { withCredentials: true }).then((r) => r.data);

/**
 * Copy a scenario's node/edge structure with blank content.
 * @route POST /api/scenarios/:scenarioId/copy-structure
 * @param {string} scenarioId
 * @returns {Promise<Object>} The newly created structure-only scenario
 */
export const copyScenarioStructure = (scenarioId) =>
    api.post(`${BASE}/${scenarioId}/copy-structure`, {}, { withCredentials: true }).then((r) => r.data);

/**
 * Fetch paginated public scenarios for the homepage.
 * @route GET /api/scenarios/homepage
 * @param {{ filterType?: string, tag?: string, filterBy?: 'public'|'protected', creator?: string,
 *           sortBy?: 'views'|'title'|'publishedAt'|'commentsCount'|'avgRating',
 *           sortOrder?: 'asc'|'desc', page?: number, limit?: number }} [params]
 * @returns {Promise<{ scenarios: Object[], currentPage: number, totalPages: number, totalScenarios: number, hasMore: boolean }>}
 */
export const getHomepageScenarios = (params) =>
    api.get(`${BASE}/homepage`, { params }).then((r) => r.data);

/**
 * Get a scenario by ID, with optional field population.
 * @route GET /api/scenarios/:scenarioId
 * @param {string} scenarioId
 * @param {string} [populate] - Comma-separated field names to populate (e.g. "creator,tags")
 * @returns {Promise<Object>} The scenario document
 */
export const getScenario = (scenarioId, populate) =>
    api.get(`${BASE}/${scenarioId}`, { params: { populate } }).then((r) => r.data);

/**
 * Check if the caller has edit access to a scenario.
 * @route GET /api/scenarios/:scenarioId/verifyOwnership
 * @param {string} scenarioId
 * @returns {Promise<{ message: string, scenarioTitle: string }>}
 */
export const verifyScenarioOwnership = (scenarioId) =>
    api.get(`${BASE}/${scenarioId}/verifyOwnership`).then((r) => r.data);

/**
 * Update a scenario's editable fields.
 * @route PATCH /api/scenarios/:scenarioId
 * @param {string} scenarioId
 * @param {Object} data - Fields to update (title, description, isPrivate, isHidden, showMap, isLogged, password, isPublished, image, audio)
 * @returns {Promise<Object>} Updated scenario document
 */
export const updateScenario = (scenarioId, data) =>
    api.patch(`${BASE}/${scenarioId}`, data).then((r) => r.data);

// The table of contents is draft state: it is authored through the canvas ops
// endpoint (`setTableOfContents`) and reaches the scenario at publish. The
// create-toc and update-toc routes that used to live here are gone.

/**
 * Add a user as a collaborator on a scenario.
 *
 * Declining is a 200, not an error: the cap (2), an existing collaborator and the
 * creator themselves all come back with an explanatory `message` and an unchanged
 * list. Callers must check `message === 'Successfully added collaborator'` rather
 * than treat any resolved promise as an addition.
 *
 * @route POST /api/scenarios/:scenarioId/add-collaborator/:userId
 * @param {string} scenarioId
 * @param {string} userId
 * @returns {Promise<{ message: string, collaborators: Object[] }>}
 */
export const addCollaborator = (scenarioId, userId) =>
    api.post(`${BASE}/${scenarioId}/add-collaborator/${userId}`).then((r) => r.data);

/**
 * Remove a collaborator from a scenario.
 * @route POST /api/scenarios/:scenarioId/remove-collaborator/:userId
 * @param {string} scenarioId
 * @param {string} userId
 * @returns {Promise<{ message: string, collaborators: Object[] }>} The list after removal
 */
export const removeCollaborator = (scenarioId, userId) =>
    api.post(`${BASE}/${scenarioId}/remove-collaborator/${userId}`).then((r) => r.data);

/**
 * Get the collaborators on a scenario, plus the creator id — the caller may be a
 * collaborator, and only the creator (or an admin) may change the list.
 * @route GET /api/scenarios/:scenarioId/get-collaborators
 * @param {string} scenarioId
 * @returns {Promise<{ collaborators: Object[], creator: string }>}
 */
export const getCollaborators = (scenarioId) =>
    api.get(`${BASE}/${scenarioId}/get-collaborators`).then((r) => r.data);

/**
 * Increment a scenario's view count.
 * @route PATCH /api/scenarios/:scenarioId/views
 * @param {string} scenarioId
 * @returns {Promise<{ message: string, scenario: Object }>}
 */
export const incrementViews = (scenarioId) =>
    api.patch(`${BASE}/${scenarioId}/views`, {}).then((r) => r.data);

/**
 * Increment a scenario's completion count.
 * @route PATCH /api/scenarios/:scenarioId/completions
 * @param {string} scenarioId
 * @returns {Promise<{ message: string, scenario: Object }>}
 */
export const incrementCompletions = (scenarioId) =>
    api.patch(`${BASE}/${scenarioId}/completions`, {}).then((r) => r.data);

/**
 * Find published scenarios by exact tag name (case-insensitive).
 * @route GET /api/scenarios/search/tags
 * @param {string} tag
 * @returns {Promise<Object[]>} Matching scenarios, or empty array if tag not found
 */
export const searchByTag = (tag) =>
    api.get(`${BASE}/search/tags`, { params: { tag } }).then((r) => r.data);

/**
 * Full-text fuzzy search across scenarios (by title) and users (by username/email).
 * @route POST /api/scenarios/improvedScenarioSearch
 * @param {string} searchTerm
 * @returns {Promise<{ info: Array<Object & { searchType: 'scenario' | 'user' }> }>}
 */
export const searchScenarios = (searchTerm) =>
    api.post(`${BASE}/improvedScenarioSearch`, { searchTerm }).then((r) => r.data);



